import { getAddress, type Address, type Hex } from "viem";
import { BRIDGE } from "../../../config/bridge";
import { recordSchema, intentSchema, claimIntentSchema } from "./schemas";
import type { BridgeRecord, RecoveryIntent, ClaimIntent, ClaimStatus, HistoryRow } from "./types";
import type { BridgeDirection } from "../types";
import type { BridgeHistorySync } from "./sync";

type StoragePort = Pick<Storage, "length" | "key" | "getItem" | "setItem"> & Partial<Pick<Storage, "removeItem">>;
export type JournalScope = { account: Address; childRpc: string; parentRpc: string };
export type ExclusiveLock = <T>(name: string, work: () => Promise<T>) => Promise<T>;
export async function browserLock<T>(name: string, work: () => Promise<T>): Promise<T> {
  if (!navigator.locks) return Promise.reject(new Error("Cross-tab recovery locking is unavailable"));
  return navigator.locks.request(name, { mode: "exclusive" }, work);
}
export class BridgeJournal {
  readonly prefix: string;
  private memory = new Map<string, BridgeRecord>();
  private pending = new Map<string, RecoveryIntent>();
  private claims = new Map<string, ClaimIntent>();
  error: string | null = null;
  sync: BridgeHistorySync | undefined;
  private persisted = new Set<string>();
  constructor(readonly scope: JournalScope, private storage: StoragePort | null, private lock: ExclusiveLock = browserLock) {
    this.prefix = `merklewood.bridge.v1:${encodeURIComponent(JSON.stringify([BRIDGE.id, scope.childRpc, scope.parentRpc, getAddress(scope.account)]))}:`;
  }
  read(): { records: BridgeRecord[]; intents: RecoveryIntent[]; claims: ClaimIntent[] } {
    try {
      if (!this.storage) throw new Error("Storage unavailable");
      for (const key of this.persisted) if (this.storage.getItem(`${this.prefix}${key}`) === null) {
        if (key.startsWith("record:")) this.memory.delete(key.slice(7));
        if (key.startsWith("intent:")) this.pending.delete(key.slice(7));
        if (key.startsWith("claim:")) this.claims.delete(key.slice(6));
        this.persisted.delete(key);
      }
      // One key per immutable identity prevents unrelated cross-tab writes losing rows.
      for (let index = 0; index < this.storage.length; index++) {
        const key = this.storage.key(index);
        if (!key?.startsWith(this.prefix)) continue;
        if (key.startsWith(`${this.prefix}sync:`)) continue;
        const raw = this.storage.getItem(key);
        if (raw === null) continue;
        const intent = key.startsWith(`${this.prefix}intent:`);
        const parsed: unknown = JSON.parse(raw);
        if (key.startsWith(`${this.prefix}claim:`)) {
          const incoming = claimIntentSchema.parse(parsed), previous = this.claims.get(incoming.id);
          if (incoming.account !== getAddress(this.scope.account) || key !== `${this.prefix}claim:${incoming.id}` || previous &&
            (previous.recordId !== incoming.recordId || previous.createdAt !== incoming.createdAt ||
              previous.transactionHash && incoming.transactionHash && previous.transactionHash !== incoming.transactionHash ||
              previous.transactionHash && incoming.outcome || previous.outcome && incoming.transactionHash)) throw new Error("Claim journal conflict");
          if (!previous?.transactionHash && !previous?.outcome) this.claims.set(incoming.id, incoming);
          this.persisted.add(`claim:${incoming.id}`);
          continue;
        }
        const value = intent ? intentSchema.parse(parsed) : recordSchema.parse(parsed);
        if (value.account !== getAddress(this.scope.account) || key !== `${this.prefix}${intent ? "intent" : "record"}:${value.id}`)
          throw new Error("Journal scope mismatch");
        if (intent) {
          const incoming = value as RecoveryIntent, previous = this.pending.get(value.id);
          if (previous && (previous.amountWei !== incoming.amountWei || previous.direction !== incoming.direction || previous.createdAt !== incoming.createdAt ||
            previous.sourceHash && incoming.sourceHash && previous.sourceHash !== incoming.sourceHash || previous.sourceHash && incoming.outcome || previous.outcome && incoming.sourceHash))
            throw new Error("Journal intent conflict");
          // Failed persistence or a delayed storage event cannot erase an already observed hash.
          if (!previous?.sourceHash && !previous?.outcome) this.pending.set(value.id, incoming);
        }
        else this.memory.set(value.id, value as BridgeRecord);
        this.persisted.add(`${intent ? "intent" : "record"}:${value.id}`);
      }
    } catch { this.error = "Browser recovery storage is unavailable or contains invalid records. Tracking remains read-only; existing records are retained."; }
    return { records: [...this.memory.values()], intents: [...this.pending.values()], claims: [...this.claims.values()] };
  }
  withRecordLock<T>(work: () => Promise<T>) {
    // Read-driven tracking is available on browsers without Web Locks. Cleanup
    // and wallet reservations still require the lock.
    if (this.lock === browserLock && !navigator.locks) return work();
    return this.lock(this.prefix, work);
  }
  hasLocal(key: string) { return this.storage?.getItem(`${this.prefix}${key}`) != null; }
  put(record: BridgeRecord): void {
    const value = recordSchema.parse(record);
    if (value.account !== getAddress(this.scope.account)) throw new Error("Journal account mismatch");
    this.memory.set(value.id, value);
    this.save(`record:${value.id}`, value);
  }
  private save(key: string, value: BridgeRecord | RecoveryIntent | ClaimIntent) {
    try {
      if (!this.storage || this.error) throw new Error("Recovery unavailable");
      this.storage.setItem(`${this.prefix}${key}`, JSON.stringify(value));
      this.persisted.add(key);
    } catch { this.error = "Recovery could not be saved in this browser. Keep transaction hashes; do not start another transfer until storage is available."; }
  }
  /** Foundation for later sends. No signature or calldata can enter the strict schema. */
  async reserve(direction: BridgeDirection, amountWei: bigint): Promise<RecoveryIntent> {
    return this.lock(this.prefix, async () => {
      this.read();
      // Each explicit deposit/withdrawal is independent of older recovery state.
      // Keep every intent and hash; the lock serializes persistence, not transfers.
      if (this.error) throw new Error("BRIDGE_RECOVERY_BLOCKED");
      const value = intentSchema.parse({ id: crypto.randomUUID(), account: this.scope.account, recipient: this.scope.account,
        direction, amountWei: amountWei.toString(), createdAt: Date.now(), sourceHash: null });
      this.pending.set(value.id, value); this.save(`intent:${value.id}`, value);
      if (this.error) throw new Error("BRIDGE_RECOVERY_BLOCKED");
      return value;
    });
  }
  /** Retain a late hash under the original journal, even after the wallet has changed. */
  async attachHash(id: string, sourceHash: Hex): Promise<void> {
    this.read();
    const original = this.pending.get(id);
    if (!original || original.outcome || original.sourceHash && original.sourceHash !== sourceHash) throw new Error("BRIDGE_RECOVERY_HASH_CONFLICT");
    const retained = intentSchema.parse({ ...original, sourceHash, submittedAt: original.submittedAt ?? Date.now() });
    this.pending.set(id, retained);
    try { await this.lock(this.prefix, async () => {
      this.read();
      const original = this.pending.get(id);
      if (!original || original.outcome || original.sourceHash && original.sourceHash !== sourceHash) throw new Error("BRIDGE_RECOVERY_HASH_CONFLICT");
      const value = intentSchema.parse({ ...original, sourceHash, submittedAt: retained.submittedAt });
      this.pending.set(value.id, value); this.save(`intent:${value.id}`, value);
      if (this.error) throw new Error("BRIDGE_RECOVERY_BLOCKED");
    }); } catch (error) {
      this.error = "Recovery could not be saved in this browser. Keep transaction hashes; do not start another transfer until storage is available.";
      throw error;
    }
    if (this.sync) {
      await this.sync.retain(retained, () => this.hasLocal(`intent:${id}`));
      // Independent of the active wallet generation: retain/report the original hash.
      this.sync.flush(new AbortController().signal).catch(() => {});
    }
  }
  reconciled(intent: RecoveryIntent): boolean {
    if (intent.outcome) return true;
    return [...this.memory.values()].some(row => this.matches(row, intent));
  }
  private matches(row: BridgeRecord, intent: RecoveryIntent) {
    return row.direct && row.sourceHash === intent.sourceHash && row.direction === intent.direction &&
      row.amountWei === intent.amountWei && row.account === intent.account && row.recipient === intent.recipient;
  }
  /** Only the owning send operation may settle a proven non-broadcast attempt. */
  async settleUnsent(id: string, outcome: "not-submitted" | "rejected") {
    return this.lock(this.prefix, async () => {
      this.read();
      const original = this.pending.get(id);
      if (!original || original.sourceHash || original.outcome) throw new Error("BRIDGE_RECOVERY_SETTLEMENT_CONFLICT");
      const value = intentSchema.parse({ ...original, outcome });
      this.pending.set(id, value); this.save(`intent:${id}`, value);
      if (this.error) throw new Error("BRIDGE_RECOVERY_BLOCKED");
    });
  }
  async reserveClaim(record: BridgeRecord, freshlyCompleted: () => ReadonlySet<string>): Promise<ClaimIntent> {
    return this.lock(this.prefix, async () => {
      const saved = this.read(), completed = freshlyCompleted();
      if (this.error || record.direction !== "out" || record.sourceReverted || record.account !== getAddress(this.scope.account) ||
        !saved.records.some(row => JSON.stringify(row) === JSON.stringify(record)) ||
        saved.intents.some(intent => !this.reconciled(intent)) || saved.claims.some(claim => !claim.outcome && !completed.has(claim.id)))
        throw new Error("Resolve pending transactions before claiming.");
      const value = claimIntentSchema.parse({ id: crypto.randomUUID(), account: record.account, recordId: record.id, createdAt: Date.now(), transactionHash: null });
      this.claims.set(value.id, value); this.save(`claim:${value.id}`, value);
      if (this.error) throw new Error("Claim recovery storage is unavailable.");
      return value;
    });
  }
  async attachClaimHash(id: string, transactionHash: Hex) {
    this.read();
    const original = this.claims.get(id);
    if (!original || original.outcome || original.transactionHash && original.transactionHash !== transactionHash) throw new Error("Claim hash conflict");
    const retained = claimIntentSchema.parse({ ...original, transactionHash });
    this.claims.set(id, retained);
    try { await this.lock(this.prefix, async () => {
      this.read(); this.save(`claim:${id}`, retained);
      if (this.error) throw new Error("Claim recovery storage is unavailable.");
    }); } catch (error) { this.error = "Claim recovery could not be saved. Keep the transaction hash; do not resend."; throw error; }
    const record = this.memory.get(retained.recordId);
    if (this.sync && record) {
      await this.sync.retainClaim(record, retained, () => this.hasLocal(`claim:${id}`));
      this.sync.flush(new AbortController().signal).catch(() => {});
    }
  }
  async settleClaimUnsent(id: string, outcome: "not-submitted" | "rejected") {
    return this.lock(this.prefix, async () => {
      this.read(); const original = this.claims.get(id);
      if (!original || original.transactionHash || original.outcome) throw new Error("Claim settlement conflict");
      const value = claimIntentSchema.parse({ ...original, outcome });
      this.claims.set(id, value); this.save(`claim:${id}`, value);
      if (this.error) throw new Error("Claim recovery storage is unavailable.");
    });
  }
  private remove(kind: "claim" | "intent" | "record", id: string) {
    const key = `${kind}:${id}`;
    try { this.storage!.removeItem!(`${this.prefix}${key}`); }
    catch (error) {
      this.error = "Recovery storage could not be cleaned up. Existing recovery is retained.";
      throw error;
    }
    this.persisted.delete(key);
    (kind === "claim" ? this.claims : kind === "intent" ? this.pending : this.memory).delete(id);
  }
  private hasOutbox() {
    for (let index = 0; index < this.storage!.length; index++) {
      const key = this.storage!.key(index);
      if (key?.startsWith(`${this.prefix}sync:`) && !key.startsWith(`${this.prefix}sync:environment:`)) return true;
    }
    return false;
  }
  async pruneUnsent(guard: () => void) {
    return this.lock(this.prefix, async () => {
      guard();
      const saved = this.read();
      if (!this.storage?.removeItem || this.error) return false;
      let changed = false;
      for (const intent of saved.intents) if (intent.outcome && !intent.sourceHash) {
        this.remove("intent", intent.id); changed = true;
      }
      const unsentClaims = saved.claims.filter(c => c.outcome && !c.transactionHash);
      const outboxUnavailable = unsentClaims.length > 0 && !this.sync && this.hasOutbox();
      for (const claim of unsentClaims) if (!outboxUnavailable && (!this.sync || this.sync.canPruneUnsentClaim(claim))) {
        this.remove("claim", claim.id); changed = true;
      }
      return changed;
    });
  }
  async pruneCompleted(record: BridgeRecord, completedClaims: ReadonlySet<string>, verified?: {
    row: HistoryRow; claims: ClaimStatus[]; current: () => boolean;
  }) {
    return this.lock(this.prefix, async () => {
      if (!this.storage?.removeItem || this.error || verified && !verified.current()) return false;
      const saved = this.read();
      const existing = saved.records.find(r => r.id === record.id);
      if (this.error || existing && JSON.stringify(existing) !== JSON.stringify(record)) return false;
      const claims = saved.claims.filter(c => c.recordId === record.id);
      if (claims.some(c => !c.outcome && (!c.transactionHash || !completedClaims.has(c.id)))) return false;
      const observedClaims = verified?.claims.filter(c => c.intent.recordId === record.id) ?? [];
      if (verified && claims.some(c => !observedClaims.some(o => JSON.stringify(o.intent) === JSON.stringify(c)))) return false;
      const hasSibling = saved.records.some(other => other.id !== record.id && other.sourceHash === record.sourceHash) ||
        saved.claims.some(claim => claim.recordId !== record.id && claim.recordId.split(":")[1] === record.sourceHash);
      const prune = (hasOtherOutbox: boolean) => {
        for (const claim of claims) this.remove("claim", claim.id);
        if (!hasSibling && !hasOtherOutbox)
          for (const intent of saved.intents) if (this.matches(record, intent)) this.remove("intent", intent.id);
        if (existing) this.remove("record", record.id);
      };
      // Outbox acknowledgement and all dependent keys are read under the same lock
      // as reservation, hash retention, record writes and HTTP acknowledgement.
      try {
        if (!this.sync) { prune(this.hasOutbox()); return true; }
        return !!verified && this.sync.forgetCompleted(verified.row, observedClaims, prune, () =>
          [...this.memory.values()].some(r => r.sourceHash === record.sourceHash) ||
          [...this.pending.values()].some(i => i.sourceHash === record.sourceHash) ||
          [...this.claims.values()].some(c => c.recordId.split(":")[1] === record.sourceHash));
      } catch { return false; }
    });
  }
}
